import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { MdLock, MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import BrandLogo from '../../components/common/BrandLogo';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { resetPassword } from '../../services/authService';
import useForceDarkTheme from '../../hooks/useForceDarkTheme';

function ResetPassword() {
  useForceDarkTheme();
  const { token } = useParams();

  // 'form' | 'success' | 'error'
  const [phase,   setPhase]   = useState('form');
  const [errMsg,  setErrMsg]  = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange' });

  const passwordValue = watch('password', '');

  const onSubmit = async ({ password: pw }) => {
    if (loading) return; // prevent duplicate submit
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setPhase('success');
    } catch (err) {
      const msg = err.response?.data?.message
        || 'This reset link is invalid or has expired. Please request a new password reset link.';
      setErrMsg(msg);
      setPhase('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <BrandLogo size={28} />
        <span className="font-bold text-secondary text-xl">TrainAxis</span>
      </div>

      <div className="bg-[#1e1d1b] rounded-2xl border border-[rgba(237,232,224,0.08)] shadow-2xl p-6 max-w-sm w-full">

        {/* ── Success ── */}
        {phase === 'success' && (
          <div className="text-center py-2">
            <div className="w-16 h-16 bg-green-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MdCheckCircle className="text-green-500 text-4xl" />
            </div>
            <h2 className="font-bold text-secondary text-xl mb-2">Password changed successfully</h2>
            <p className="text-[#8a847c] text-sm mb-6">
              You can now sign in with your new password.
            </p>
            <Link to="/login">
              <Button fullWidth>Go to Login</Button>
            </Link>
          </div>
        )}

        {/* ── Invalid / expired token ── */}
        {phase === 'error' && (
          <div className="text-center py-2">
            <div className="w-16 h-16 bg-red-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MdErrorOutline className="text-red-400 text-4xl" />
            </div>
            <h2 className="font-bold text-secondary text-xl mb-2">Link invalid or expired</h2>
            <p className="text-[#8a847c] text-sm mb-6">
              {errMsg}
            </p>
            <Link to="/forgot-password">
              <Button fullWidth>Request New Link</Button>
            </Link>
            <div className="mt-3">
              <Link to="/login" className="text-xs text-[#8a847c] hover:text-primary transition-colors">
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {/* ── Form ── */}
        {phase === 'form' && (
          <>
            <h2 className="font-bold text-secondary text-xl mb-1">Set New Password</h2>
            <p className="text-[#8a847c] text-sm mb-5">Enter and confirm your new password below.</p>
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              <Input
                icon={MdLock}
                type="password"
                placeholder="New password (min 8 chars)"
                autoComplete="new-password"
                error={errors.password?.message}
                {...register('password', {
                  required:  'Password is required.',
                  minLength: { value: 8, message: 'Password must be at least 8 characters.' },
                })}
              />
              <Input
                icon={MdLock}
                type="password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                error={errors.confirmPassword?.message}
                {...register('confirmPassword', {
                  required: 'Please confirm your password.',
                  validate:  v => v === passwordValue || 'Passwords do not match.',
                })}
              />
              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!isValid}
              >
                {loading ? 'Changing password...' : 'Change Password'}
              </Button>
            </form>
            <p className="text-center text-sm text-[#8a847c] mt-4">
              <Link to="/login" className="text-primary font-semibold hover:underline">Back to Login</Link>
            </p>
          </>
        )}

      </div>
    </div>
  );
}

export default ResetPassword;
